import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { BaseParams, DetailsParams } from '@interfaces/app/nav.interfaces';
import { OrderHeaderRow } from '@interfaces/order';
import { QualityReport } from '@interfaces/report/qualityReport';
import { RollMap } from '@interfaces/report/rollMap';
import { Observable, catchError, combineLatest, map, mergeMap, of, startWith, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

import { fixOrderHeaderRows } from '@services/api.fixes';
import { NavService } from '@services/nav.service';
import { flattenResponses, getById } from '@services/utils';

@Injectable({ providedIn: 'root' })
export class ReportsService {
  http = inject(HttpClient);
  navService = inject(NavService);

  getOrderIdsWithReports(baseParams: BaseParams): Observable<string[] | null> {
    const { customerId, dateFrom, dateTo } = baseParams;
    if (!customerId) return of(null);

    return this.navService.erpCustomerIds$.pipe(
      map((ids) => {
        const requests$ = ids.map((erpCustomerId) => {
          const url = `${environment.reportApi}/reports/ordersWithReports/customers/${customerId}`;
          const params = {
            erpCustomerId,
            dateFrom: dateFrom,
            dateTo: new Date(new Date(dateTo).valueOf() + 2).toISOString(),
          };
          return this.http.get<string[]>(url, { params });
        });

        return combineLatest(requests$);
      }),
      mergeMap((response) => response),
      map(flattenResponses),
    );
  }

  getOrders(baseParams: BaseParams): Observable<OrderHeaderRow[] | null> {
    const { customerId, sourceSystemCodes } = baseParams;

    let codes = sourceSystemCodes;
    codes = sourceSystemCodes.filter((id) => id !== '5');
    codes.push('6');

    return combineLatest(
      codes.map((sourceSystemCode) => {
        const url = `${environment.orderApi}/customers/${customerId}/orderrows`;
        const params = {
          sourcesystemid: sourceSystemCode,
          orderstatuses: ['Confirmed', 'Pending', 'New', 'Produced', 'Dispatched', 'Delivered'],
          ordereddatefrom: baseParams.dateFrom,
          ordereddateto: baseParams.dateTo,
        };

        return this.http.get<OrderHeaderRow[]>(url, { params }).pipe(
          startWith(null),
          map((orderHedaerRows) => fixOrderHeaderRows(orderHedaerRows)),
          catchError(() => of([] as OrderHeaderRow[])),
        );
      }),
    ).pipe(
      tap((responses) => this.navService.toggleLoadingBar(responses)),
      map(flattenResponses),
    );
  }

  getOrdersWithQualityReports(baseParams: BaseParams): Observable<OrderHeaderRow[] | null> {
    return combineLatest([
      this.getOrders(baseParams),
      this.getOrderIdsWithReports(baseParams),
    ]).pipe(
      map(([orders, orderIdsWithReports]): OrderHeaderRow[] | null => {
        if (!orders || !orderIdsWithReports || !Array.isArray(orderIdsWithReports)) return null;

        if (orderIdsWithReports.length === 0) return [] as OrderHeaderRow[];

        const ordersWithReports = orders.reduce((acc: OrderHeaderRow[], order: OrderHeaderRow) => {
          if (
            orderIdsWithReports &&
            orderIdsWithReports?.includes(`${order.orderNumber}-${order.orderRowNumber}`)
          ) {
            acc = [...acc, order];
          }
          return acc;
        }, []);

        return ordersWithReports;
      }),
    );
  }

  getOrdersWithoutQualityReports(baseParams: BaseParams): Observable<OrderHeaderRow[] | null> {
    return combineLatest([
      this.getOrders(baseParams),
      this.getOrderIdsWithReports(baseParams),
    ]).pipe(
      map(([orders, orderIdsWithReports]): OrderHeaderRow[] | null => {
        if (!orders || !orderIdsWithReports || !Array.isArray(orderIdsWithReports)) return null;

        if (orderIdsWithReports.length === 0) return orders;

        const ordersWithoutReports = orders.filter(
          (order) =>
            orderIdsWithReports.indexOf(`${order.orderNumber}-${order.orderRowNumber}`) === -1,
        );

        return ordersWithoutReports;
      }),
    );
  }

  getQualityReport(detailsParams: DetailsParams): Observable<QualityReport | null> {
    const { customerId, orderNumber, orderRowNumber } = detailsParams;
    const url = `${environment.reportApi}/reports/quality/customers/${customerId}/${orderNumber}-${orderRowNumber}`;

    return this.http.get<QualityReport>(url).pipe(
      startWith(null),
      // Quality reports return 204 / null when no reports are found.
      // Return [] to stop spinner.
      map((report, index) => {
        if (index === 0) return null;
        if (!report)
          return {
            properties: [],
          };
        return report;
      }),
      catchError(() => of({ properties: [] })),
    );
  }

  getRollMap(detailsParams: DetailsParams): Observable<RollMap | null> {
    const { customerId, orderNumber, orderRowNumber } = detailsParams;
    const url = `${environment.reportApi}/reports/rollmap/customers/${customerId}/${orderNumber}-${orderRowNumber}`;

    return this.http.get<RollMap>(url).pipe(startWith(null));
  }

  getOrderHeaderRowById(detailsParams: DetailsParams): Observable<OrderHeaderRow | null> {
    const { orderNumber, orderRowNumber } = detailsParams;
    return this.getOrders(detailsParams).pipe(
      startWith(null),
      map((orders) =>
        getById(orders || [], { orderNumber, orderRowNumber: parseInt(orderRowNumber) }),
      ),
    );
  }
}
